import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import { createContext, useMemo, useState } from 'react';
import type React from 'react';
import { SpinnerType } from '~/contracts/vdp';
import { DeepPartial } from '~/types';

type DummyFnType = () => void;

export type ContextType = {
  hasError: boolean;
  isInTourMode: boolean;
  isSpinnerDisabled: boolean;
  isSpinnerImmersive: boolean;
  isSpinnerScriptInserted: boolean;
  openHotspot: (id: number, openOnlyHotspotCateg?: boolean) => void;
  openImmersive: () => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  setIsInTourMode: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSpinnerImmersive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSpinnerScriptInserted: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenHotspot: React.Dispatch<React.SetStateAction<DummyFnType>>;
  setOpenImmersive: React.Dispatch<React.SetStateAction<DummyFnType>>;
  setShouldFetchSpinner: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldHideSpinner: React.Dispatch<React.SetStateAction<boolean>>;
  setSpinnerType: React.Dispatch<React.SetStateAction<SpinnerType>>;
  shouldFetchSpinner: boolean;
  shouldHideSpinner: boolean;
  spinnerReady: LoadableState<boolean>;
  spinnerType: SpinnerType;
};

const defaultContext: ContextType = {
  hasError: false,
  isInTourMode: false,
  isSpinnerDisabled: false,
  isSpinnerImmersive: false,
  isSpinnerScriptInserted: false,
  openHotspot() {},
  openImmersive() {},
  setHasError() {},
  setIsInTourMode() {},
  setIsSpinnerImmersive() {},
  setIsSpinnerScriptInserted() {},
  setOpenHotspot() {},
  setOpenImmersive() {},
  setShouldFetchSpinner() {},
  setShouldHideSpinner() {},
  setSpinnerType() {},
  shouldFetchSpinner: false,
  shouldHideSpinner: false,
  spinnerReady: defaultLoadableState(false),
  spinnerType: ''
};

const SpinnerContext = createContext(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  isSpinnerDisabled: boolean;
}>;

export function SpinnerContextProvider({ ...props }: OwnProps) {
  const spinnerReady = useLoadableState(props.isSpinnerDisabled, {
    initializeAsLoaded: props.isSpinnerDisabled
  });
  const [shouldFetchSpinner, setShouldFetchSpinner] = useState(false);
  const [shouldHideSpinner, setShouldHideSpinner] = useState(false);
  const [openImmersive, setOpenImmersive] = useState<DummyFnType>(
    () => defaultContext.openImmersive
  );
  const [openHotspot, setOpenHotspot] = useState<DummyFnType>(
    () => defaultContext.openHotspot
  );
  const [isSpinnerScriptInserted, setIsSpinnerScriptInserted] = useState(
    defaultContext.isSpinnerScriptInserted
  );
  const [isSpinnerImmersive, setIsSpinnerImmersive] = useState(
    defaultContext.isSpinnerImmersive
  );
  const [isInTourMode, setIsInTourMode] = useState(defaultContext.isInTourMode);
  const [spinnerType, setSpinnerType] = useState(defaultContext.spinnerType);
  const [hasError, setHasError] = useState(defaultContext.hasError);

  const value = useMemo<ContextType>(
    () => ({
      hasError,
      isInTourMode,
      isSpinnerDisabled: props.isSpinnerDisabled,
      isSpinnerImmersive,
      isSpinnerScriptInserted,
      openHotspot,
      openImmersive,
      setHasError,
      setIsInTourMode,
      setIsSpinnerImmersive,
      setIsSpinnerScriptInserted,
      setOpenHotspot,
      setOpenImmersive,
      setShouldFetchSpinner,
      setShouldHideSpinner,
      setSpinnerType,
      shouldFetchSpinner,
      shouldHideSpinner,
      spinnerReady,
      spinnerType
    }),
    [
      hasError,
      isInTourMode,
      isSpinnerImmersive,
      isSpinnerScriptInserted,
      openHotspot,
      openImmersive,
      props.isSpinnerDisabled,
      shouldFetchSpinner,
      shouldHideSpinner,
      spinnerReady,
      spinnerType
    ]
  );

  return (
    <SpinnerContext.Provider value={value}>
      {props.children}
    </SpinnerContext.Provider>
  );
}

SpinnerContextProvider.displayName = 'SpinnerContextProvider';

export const generateSpinnerContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export default SpinnerContext;
